import { useContext, useEffect } from "react"
import { Field, FastField, useFormikContext } from "formik"
import { FormContext } from "../../context/formContext"
import { InputText as Input } from "primereact/inputtext"
import { UserContext } from "../../context/userContext"
import fieldVisibility from "../../helpers/fieldVisibility"
import _ from "lodash"
import { TRIM_TYPES, trimTextValue } from "./helpers/trimTextValue"
import styles from "../../styles/Form.module.css"

function RegularFieldOutput(props) {
  const { field, isRequired, block, index } = props
  const form = useContext(FormContext)
  const validation = field.field_validation
  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled
  const formik = useFormikContext()
  const user = useContext(UserContext)

  // Sync the external ID when it changes.
  useEffect(() => {
    if (field.field_name === "manage_external_id" && user.participant_external_id &&
      formik.values.manage_external_id !== user.participant_external_id) {
      formik.setFieldValue("manage_external_id", user.participant_external_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.participant_external_id])

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => {
            const decodedValue = document.createElement("textarea")
            decodedValue.innerHTML = formikField.value
            if (_.isEmpty(validation?.maxlength)) {
              return (
                <>
                  <Input
                    name={formikField.name}
                    id={formikField.name}
                    value={decodedValue.value}
                    onChange={formikField.onChange}
                    disabled={isDisabled}
                    onBlur={(e) => {
                      formik.setFieldValue(
                        formikField.name,
                        trimTextValue(e.target.value, TRIM_TYPES.BOTH),
                        true,
                      )
                      formikField.onBlur(e)
                    }}
                    autoComplete="off"
                  />
                  {meta.touched && meta.error && (
                    <div className={styles.errFieldMsg}>{meta.error}</div>
                  )}
                </>
              )
            } else {
              return (
                <>
                  <Input
                    name={formikField.name}
                    id={formikField.name}
                    value={decodedValue.value}
                    maxLength={validation?.maxlength}
                    onChange={formikField.onChange}
                    disabled={isDisabled}
                    onBlur={(e) => {
                      formik.setFieldValue(
                        formikField.name,
                        trimTextValue(e.target.value, TRIM_TYPES.BOTH),
                        true,
                      )
                      formikField.onBlur(e)
                    }}
                    autoComplete="off"
                  />
                  {meta.touched && meta.error && (
                    <div className={styles.errFieldMsg}>{meta.error}</div>
                  )}
                </>
              )
            }
          }}
        </Field>
      </div>
    </div>
  )
}
function MultiFieldOutput(props) {
  const { field, isRequired, index, mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
  const formik = useFormikContext()
  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, meta }) => {
            return (
              <>
                <Input
                  name={fastField.name}
                  id={fastField.name}
                  value={fastField.value}
                  onChange={fastField.onChange}
                  onBlur={(e) => {
                    formik.setFieldValue(
                      fastField.name,
                      trimTextValue(e.target.value, TRIM_TYPES.BOTH),
                      true,
                    )
                    fastField.onBlur(e)
                  }}
                  autoComplete="off"
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}
function NestedMultiFieldOutput(props) {
  const {
    field,
    isRequired,
    index,
    mfFieldName,
    mfIndex,
    nestedMfFieldName,
    nestedMfIndex,
  } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${nestedMfFieldName}.${nestedMfIndex}.${field.field_name}`
  const formik = useFormikContext()
  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, meta }) => {
            return (
              <>
                <Input
                  name={fastField.name}
                  id={fastField.name}
                  value={fastField.value}
                  onChange={fastField.onChange}
                  onBlur={(e) => {
                    formik.setFieldValue(
                      fastField.name,
                      trimTextValue(e.target.value, TRIM_TYPES.BOTH),
                      true,
                    )
                    fastField.onBlur(e)
                  }}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

export default function TypeText(props) {
  const checkIfRequired = () => {
    try {
      return JSON.parse(props.field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }
  const isRequired = checkIfRequired()
  switch (props.type) {
    case "regular-field":
      const regFieldProps = {
        field: props.field,
        isRequired,
        block: props.block,
        index: props.index,
      }
      return <RegularFieldOutput {...regFieldProps} />
    case "multi-field":
      const multiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    case "nested-multi-field":
      const nestedMultiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        nestedMfFieldName: props.nestedMfFieldName,
        nestedMfIndex: props.nestedMfIndex,
      }
      return <NestedMultiFieldOutput {...nestedMultiFieldProps} />
    default:
      return null
  }
}
